import type { Country, StateOption } from '@/api/geography.api';
import { normalizeUrl } from '@/lib/utils/url.utils';
import type {
  StartupFoundingTeamMember,
  StartupProfileData,
  StartupProfileSector,
} from '@/features/profiles/api/startup-profile.api';
import type { Sector } from './api/taxonomy.api';
import type { CreateStartupFormValues } from './schema';

/** Reference data the review resolves the form's ids against. */
export interface ReviewLookups {
  sectors: Sector[];
  countries: Country[];
  states: StateOption[];
}

/**
 * `form.watch()` is RAW form state: zod's coercion only runs on submit, so a
 * number field can still be a string, and a cleared one `''`. Both read as
 * "not provided" here, never as 0.
 */
function toNumber(value: unknown): number | null {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function toText(value: string | undefined): string | null {
  return value?.trim() || null;
}

/** A URL shown exactly as Save will store it (`https://` added to a bare host). */
function toUrl(value: string | undefined): string | null {
  return normalizeUrl(value ?? '') || null;
}

function toList<T>(value: T[] | undefined): T[] {
  // RHF reports an untouched checkbox group as `false`, not `[]`.
  return Array.isArray(value) ? value : [];
}

function toSectors(values: CreateStartupFormValues, sectors: Sector[]): StartupProfileSector[] {
  const sector = sectors.find((row) => row.id === toNumber(values.sectorId));
  if (!sector) return [];
  const subsectorIds = toList(values.subsectorIds).map(Number);
  return [
    { sectorId: sector.id, sector: sector.name, isPrimary: true },
    ...sector.subsectors
      .filter((subsector) => subsectorIds.includes(subsector.id))
      .map((subsector) => ({
        sectorId: sector.id,
        sector: sector.name,
        subsectorId: subsector.id,
        subsector: subsector.name,
        isPrimary: false,
      })),
  ];
}

function toFoundingTeam(values: CreateStartupFormValues): StartupFoundingTeamMember[] {
  // Same rule as the create payload: a row only counts once it has a name.
  return toList(values.foundingTeam)
    .filter((member) => member.name?.trim())
    .map((member) => ({
      name: member.name?.trim() ?? '',
      title: member.title?.trim() ?? '',
      linkedinUrl: toUrl(member.linkedinUrl),
      pastExits: member.pastExits === undefined ? null : member.pastExits === 'yes',
    }));
}

type ReviewData = StartupProfileData;

/** HQ ids resolved to names against the reference lists. */
function toReviewHq(values: CreateStartupFormValues, lookups: ReviewLookups): Partial<ReviewData> {
  const country = lookups.countries.find((row) => row.id === toNumber(values.hqCountryId));
  const state = lookups.states.find((row) => row.id === toNumber(values.hqStateId));
  return {
    hqCountryId: country?.id ?? null,
    hqStateId: state?.id ?? null,
    hqCityId: toNumber(values.hqCityId),
    hqCountry: country?.name ?? null,
    hqState: state?.name ?? null,
    hqCity: toText(values.hqCityName),
    hqWorldRegion: country?.worldRegion ?? null,
  };
}

/** Revenue, traction and the current raise. */
function toReviewFinancials(
  values: CreateStartupFormValues,
): Partial<ReviewData> & Pick<ReviewData, 'hasLeadInvestor'> {
  const hasLeadInvestor = values.hasLeadInvestor === 'yes';
  return {
    totalRevenueRange: toText(values.totalRevenueRange),
    arr: toNumber(values.arr),
    amountRaisedToDate: toNumber(values.amountRaisedToDate),
    nondilutiveFunding: toNumber(values.nondilutiveFunding),
    revenueModel: toText(values.revenueModel),
    revenueGrowthRate: toText(values.revenueGrowthRate),
    payingCustomers: toText(values.payingCustomers),
    useOfFunds: toText(values.useOfFunds),
    investorTypesSought: toList(values.investorTypesSought),
    financeSoughtLow: toNumber(values.financeSoughtLow),
    financeSoughtHigh: toNumber(values.financeSoughtHigh),
    instrument: toText(values.instrument),
    targetCloseDate: toText(values.targetCloseDate),
    valuationOrCap: toNumber(values.valuationOrCap),
    hasLeadInvestor,
    leadInvestorName: hasLeadInvestor ? toText(values.leadInvestorName) : null,
  };
}

/** Founding team and its self-identification answers. */
function toReviewTeam(values: CreateStartupFormValues): Partial<ReviewData> {
  return {
    foundingTeam: toFoundingTeam(values),
    founderFemale: values.founderFemale ?? null,
    founderUnderrepresented: values.founderUnderrepresented ?? null,
    founderUnderrepresentedCategories:
      values.founderUnderrepresented === 'yes'
        ? toList(values.founderUnderrepresentedCategories)
        : [],
  };
}

/**
 * Maps the create wizard's in-progress values onto the saved-profile shape, so
 * the Review step can render the very same read-only sections the startup
 * profile page shows (UX sheet r153) - the founder reviews what they will see.
 *
 * Args: values - raw `form.watch()` state; lookups - sectors, countries and
 * the selected country's states, to resolve the ids the form stores to names.
 * Returns: a `StartupProfileData` with no id (nothing is saved yet). The pitch
 * deck carries its upload key but no download link: it is not presigned until
 * the profile exists. URLs are shown normalised exactly as Save stores them.
 */
export function toReviewProfileData(
  values: CreateStartupFormValues,
  lookups: ReviewLookups,
): ReviewData {
  return {
    id: '',
    entityId: '',
    companyName: values.companyName?.trim() ?? '',
    briefDescription: toText(values.briefDescription),
    companyWebsiteUrl: toUrl(values.companyWebsiteUrl),
    companyLinkedinUrl: toUrl(values.companyLinkedinUrl),
    logoUrl: values.logoUrl ?? null,
    fundingStage: toText(values.fundingStage),
    yearFounded: toNumber(values.yearFounded),
    employeeCount: toNumber(values.employeeCount),
    ...toReviewHq(values, lookups),
    sectors: toSectors(values, lookups.sectors),
    sectorOther: toText(values.sectorOther),
    marketModels: toList(values.marketModels),
    recentMilestone: toText(values.recentMilestone),
    aiClassification: toText(values.aiClassification),
    pitchDeckUrl: toText(values.pitchDeckFileKey),
    pitchDeckFileSizeBytes: toNumber(values.pitchDeckFileSizeBytes),
    pitchDeckDownloadUrl: null,
    pitchVideoUrl: toUrl(values.pitchVideoUrl),
    ...toReviewFinancials(values),
    ...toReviewTeam(values),
  };
}
